import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const CreateMemberModal = ({ onClose, onUserCreated, tenantId }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    emergencyContact: '',
    emergencyPhone: '',
    membershipPlan: 'basic',
    billingCycle: 'monthly',
    startDate: new Date()?.toISOString()?.split('T')?.[0]
  });
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMembershipPlans();
  }, [tenantId]);

  const loadMembershipPlans = async () => {
    try {
      const { data, error } = await supabase
        ?.from('membership_plans')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.eq('is_active', true)
        ?.order('display_order', { ascending: true });

      if (error) throw error;
      setMembershipPlans(data || []);
    } catch (err) {
      console.error('Error loading membership plans:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate form
      if (!formData?.fullName || !formData?.email) {
        throw new Error('Please fill in all required fields');
      }

      // Generate a temporary password
      const tempPassword = `Gym${Math.random()?.toString(36)?.slice(-8)}!`;

      // Step 1: Create user in Supabase Auth
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: tempPassword,
        options: {
          data: {
            full_name: formData?.fullName,
            phone: formData?.phone || '',
            role: 'member'
          },
          emailRedirectTo: `${window.location?.origin}/login`
        }
      });

      if (authError) throw authError;

      // Step 2: Wait for trigger to create user_profiles
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Step 3: Create tenant membership
      const { error: membershipError } = await supabase?.from('tenant_members')?.insert({
        tenant_id: tenantId,
        user_id: authData?.user?.id,
        role: 'member',
        membership_plan: formData?.membershipPlan,
        is_active: true
      });

      if (membershipError) throw membershipError;

      // Step 4: Get selected membership plan details
      const selectedPlan = membershipPlans?.find(p => p?.plan_code === formData?.membershipPlan) || 
        membershipPlans?.find(p => p?.plan_name?.toLowerCase() === formData?.membershipPlan?.toLowerCase());

      // Calculate end date based on billing cycle
      const endDate = new Date(formData?.startDate);
      if (formData?.billingCycle === 'monthly') {
        endDate?.setMonth(endDate?.getMonth() + 1);
      } else if (formData?.billingCycle === 'quarterly') {
        endDate?.setMonth(endDate?.getMonth() + 3);
      } else if (formData?.billingCycle === 'semi_annual') {
        endDate?.setMonth(endDate?.getMonth() + 6);
      } else if (formData?.billingCycle === 'annual') {
        endDate?.setFullYear(endDate?.getFullYear() + 1);
      }

      // Calculate next billing date (same as end date for first cycle)
      const nextBillingDate = new Date(endDate);

      // Step 5: Create subscription record with all required fields
      const { error: subscriptionError } = await supabase?.from('member_subscriptions')?.insert({
        tenant_id: tenantId,
        member_id: authData?.user?.id,
        plan_id: selectedPlan?.id,
        status: 'active',
        start_date: formData?.startDate,
        end_date: endDate?.toISOString()?.split('T')?.[0],
        next_billing_date: nextBillingDate?.toISOString()?.split('T')?.[0],
        billing_cycle: formData?.billingCycle,
        monthly_price: selectedPlan?.price || 0,
        final_price: selectedPlan?.price || 0,
        auto_renew: true
      });

      if (subscriptionError) throw subscriptionError;

      onUserCreated?.();
    } catch (err) {
      setError(err?.message || 'Failed to create member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Icon name="UserPlus" size={24} color="var(--color-primary)" />
            Create New Member
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  value={formData?.fullName}
                  onChange={(e) => handleChange('fullName', e?.target?.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-destructive">*</span>
                </label>
                <Input
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleChange('email', e?.target?.value)}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleChange('phone', e?.target?.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Emergency Contact
                </label>
                <Input
                  type="text"
                  value={formData?.emergencyContact}
                  onChange={(e) => handleChange('emergencyContact', e?.target?.value)}
                  placeholder="Jane Doe"
                />
              </div>
            </div>
          </div>

          {/* Membership Details */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Membership Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Membership Plan <span className="text-destructive">*</span>
                </label>
                <select
                  value={formData?.membershipPlan}
                  onChange={(e) => handleChange('membershipPlan', e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                >
                  {membershipPlans?.length > 0 ? (
                    membershipPlans?.map(plan => (
                      <option key={plan?.id} value={plan?.plan_code}>
                        {plan?.plan_name} - ${plan?.price}/{plan?.billing_cycle}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="basic">Basic - $29/month</option>
                      <option value="premium">Premium - $59/month</option>
                      <option value="elite">Elite - $99/month</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Billing Cycle <span className="text-destructive">*</span>
                </label>
                <select
                  value={formData?.billingCycle}
                  onChange={(e) => handleChange('billingCycle', e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Start Date <span className="text-destructive">*</span>
                </label>
                <Input
                  type="date"
                  value={formData?.startDate}
                  onChange={(e) => handleChange('startDate', e?.target?.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg flex gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm text-foreground">
              <p className="font-semibold mb-1">Account Creation</p>
              <p className="text-muted-foreground">
                A temporary password will be generated and sent to the member's email. They can change it after first login.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={20} className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Icon name="UserPlus" size={20} />
                  Create Member
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMemberModal;